<script setup>
import { computed } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useModals } from "@/composables/useModals";
import { normalizeCity, normalizeApartment, normalizeHouse, normalizeStreet } from "@/utils/formatter";
import BaseCopyTooltip from "@/components/ui/BaseCopyTooltip.vue";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";

const { openModal, modalInfo } = useModals();

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const userReferraLink = computed(() => storeRequests.userData?.referral_link || "");
const userReferraCode = computed(() => (storeRequests.userData?.referral_code ? `${storeSettings.tgUrl}?ref=${storeRequests.userData?.referral_code}` : storeSettings.tgUrl));
const isMobile = computed(() => storeSettings.widthWindow <= 480);
const fullStringAddress = computed(() => {
	const address = storeRequests.userData?.address;

	if (!address) return '';

	const parts = [];

	const city = normalizeCity(address.city);
	if (city) parts.push(`г. ${city}`);

	const street = normalizeStreet(address.street);
	if (street) parts.push(`ул. ${street}`);

	const house = normalizeHouse(address.house);
	if (house) parts.push(`д. ${house}`);

	const apartment = normalizeApartment(address.apartment);
	if (apartment) parts.push(`кв. ${apartment}`);

	if(address.zip_code) parts.push(address.zip_code);

	return parts.join(', ');
});

const formatPhoneNumber = (phone) => {
	const formattedPhoneNumber = phone?.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, "7 $2 $3 $4$5");
	return formattedPhoneNumber;
};

const copy = async (text, show) => {
	await navigator.clipboard.writeText(text);
	show();
};

</script>
<template>
	<div class="user-data">
		<div class="user-data__inner">
			<div class="user-data__contacts">
				<h1 class="user-data__title">{{ storeRequests.userData?.first_name }} {{ storeRequests.userData?.last_name }}</h1>
				<div class="user-data__contacts-phone-email">
					<span>{{ storeRequests.userData?.email }}</span>
					<span>{{ formatPhoneNumber(storeRequests.userData?.phone_number) }}</span>
				</div>
				<div class="user-data__inner-btns">
					<span v-if="storeRequests.userData?.address ">{{ fullStringAddress }}</span>
					<button v-else  @click="openModal('changeUserProfile')" class="user-data__btn btn --small">Добавить адрес</button>
					<BaseTooltip v-if="!storeRequests.userData?.address " class="user-data__tooltip" :placement="isMobile ? 'left' : 'right'" content="Для доставки физических призов заполните форму">
						<svg width="24" height="24"><use href="#info"></use></svg>
					</BaseTooltip>
				</div>
			</div>
			<div class="user-data__invations" v-if="!storeSettings.isTelegramPage">
				<p class="user-data__invations-title">
					Приглашайте друзей и получайте <span>+30</span>
					за каждого друга после его регистрации по Вашей ссылке и регистрации валидного чека.<br>
					Друг получит <span>+10</span> на тех же условиях.
				</p>
				<div class="user-data__invations-btns" >
					<BaseCopyTooltip class="btn --small" title="Cкопировать" text="Ссылка скопирована">
						<template #default="{ show }">
							<span @click="copy(userReferraLink, show)">
								https://...
								<svg width="32" height="32"><use href="#copy"></use></svg>
							</span>
						</template>
					</BaseCopyTooltip>
					<BaseCopyTooltip class="btn --small" title="Cкопировать" text="Ссылка скопирована">
						<template #default="{ show }">
							<span @click="copy(userReferraCode, show)">
								Telegram
								<svg width="32" height="32"><use href="#telegram"></use></svg>
							</span>
						</template>
					</BaseCopyTooltip>
				</div>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.user-data {
	&__inner {
		display: grid;
		grid-template-columns: 1fr 45rem;
		gap: 1rem;
		align-items: start;
		@media (max-width: vars.$xx) {
			grid-template-columns: 1fr;
			gap: 3rem;
		}
	}

	&__contacts {
		padding-top: 2rem;
		@media (max-width: vars.$x480) {
			padding-top: 0;
		}
		span {
			font-weight: 500;
			font-size: 2rem;
			line-height: 120%;
			@media (max-width: vars.$x480) {
				font-size: 1.6rem;
				line-height: 140%;
			}
		}
	}

	&__title {
		font-weight: 500;
		font-size: 4rem;
		line-height: 160%;
		color: var(--text-inverse-100);
		margin-bottom: 1rem;
		@media (max-width: vars.$x480) {
			font-size: 2.4rem;
			margin-bottom: 0.5rem;
		}
	}

	&__contacts-phone-email {
		display: flex;
		align-items: center;
		margin-bottom: 2rem;
		@media (max-width: vars.$t) {
			flex-direction: column;
			align-items: start;
			gap: 1.5rem;
		}
		@media (max-width: vars.$x480) {
			gap: 0.5rem;
			margin-bottom: 0.5rem;
		}
		span {
			display: inline-block;
			&:first-child {
				position: relative;
				padding-right: 2.4rem;
				margin-right: 2.4rem;
				@media (max-width: vars.$t) {
					margin-right: 0;
					padding-right: 0;
				}
				&::before {
					position: absolute;
					content: "";
					width: 4px;
					height: 4px;
					border-radius: 100%;
					background-color: var(--fill-accent-2-1);
					top: 50%;
					right: 0;
					transform: translateY(-50%);
					@media (max-width: vars.$t) {
						display: none;
					}
				}
			}
		}
	}

	&__inner-btns {
		display: flex;
		align-items: center;
		gap: 10px;
		@media (max-width: vars.$x480) {
			margin-top: 1.4rem;
		}

	}

	&__tooltip {
		flex-shrink: 0;
	}

	&__btn {
		width: fit-content;
		@media (max-width: vars.$x480) {
			
			width: 100%;
			font-size: 2rem;
			min-height: 4.8rem;
		}
	}

	&__btn-text {
		margin-top: 1rem;
	}

	&__invations {
		@extend .base-bg;
		border: 1px solid var(--border-accent-2-3);
		border-radius: 2.4rem;
		padding: 3.2rem 2.4rem 2.4rem;
		@media (max-width: vars.$x480) {
			    padding: 2.8rem 2.2rem 2.4rem;
		}
	}

	&__invations-title {
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 144%;
		color: var(--text-inverse-100);
		margin-bottom: 2rem;
		@media (max-width: vars.$x480) {
			font-size: 1.4rem;
			line-height: 134%;
			    margin-bottom: 1.4rem;
		}

		span {
			position: relative;
			background-image: url("@/assets/img/icons/leaf.svg");
			background-repeat: no-repeat;
			background-position: 3.3rem 0px;
			background-size: contain;
			width: 5.5rem;
			display: inline-block;
			&:last-child {
				background-position: 3rem 0px;
			}
		}
		br {
			@media (max-width: vars.$x480) {
				display: none;
			}
		}
	}

	&__invations-btns {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.5rem;

		.btn {
			min-height: 3.2rem;
			font-size: 1.4rem;
			border-radius: 1rem;
			@media (max-width: vars.$x480) {
			}
			span {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;

				svg {
					width: 2.4rem;
					height: 2.4rem;
					@media (max-width: vars.$x480) {
					}
				}
			}
		}
	}
}
</style>
